import { FC } from 'react';

import { BarChartHorizontalSkeleton } from './BarChartHorizontalSkeleton';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

import { Skeleton } from '@/components';
import { CHARTS_TEST_IDS } from '@/constants/data-testids';
import { cn } from '@/utils/tailwind';

import { Card, CardContent } from '../../atoms/Card/Card';

type TickProps = {
  x: number | string;
  y: number | string;
  payload: {
    coordinate: number;
    value: string;
    index: number;
    offset: number;
    tickCoord: number;
    isShow: boolean;
  };
  size?: 'small' | 'medium' | 'large';
};

export type BarCharHorizontalDataItem = {
  name: string;
  value: number;
  color?: string;
};

type BarChartHorizontalProps = {
  title?: string;
  isLoading?: boolean;
  size?: 'small' | 'medium' | 'large';
  xKey: string;
  yKey: string;
  data: BarCharHorizontalDataItem[];
  isEditCellSkeleton?: boolean;
};

const yAxisWidths = {
  small: 80,
  medium: 164,
  large: 240,
};

const DEFAULT_LINE_COLOR = '#B3E5D2';

const DEBOUNCE_TIME = 50;

const CustomYAxisTick = ({ x, y, payload, size }: TickProps) => {
  const shouldTrim = size === 'small';
  const maxChars = 12;

  const text =
    shouldTrim && payload.value.length > maxChars
      ? payload.value.substring(0, maxChars - 1) + '…'
      : payload.value;

  return (
    <text x={x} y={y} dy={4} textAnchor="end" fill="#79716B" color="#79716B" fontSize={12}>
      {text}
    </text>
  );
};

const createCustomYAxisTick = (size: string) =>
  function CustomYAxisTickWrapper(props: any) {
    return <CustomYAxisTick {...props} size={size} />;
  };

const BAR_HEIGHT = 35.6;

const staticChartMountClassName = cn('pointer-events-none', '[&_*]:outline-none');

type SkeletonYTickProps = { x: number | string; y: number | string; yAxisWidth: number };

const BarChartSkeletonYAxisTick: FC<SkeletonYTickProps> = ({ x, y, yAxisWidth }) => (
  <g transform={`translate(${x}, ${y})`}>
    <foreignObject x={-yAxisWidth + 10} y={-10} width={yAxisWidth - 20} height={20}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Skeleton className="w-full h-4" />
      </div>
    </foreignObject>
  </g>
);

type SkeletonXYProps = { x: number | string; y: number | string };

const BarChartSkeletonXAxisTick: FC<SkeletonXYProps> = ({ x, y }) => (
  <g transform={`translate(${x}, ${y})`}>
    <foreignObject x={-10} y={0} width={20} height={20}>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Skeleton className="w-4 h-3" />
      </div>
    </foreignObject>
  </g>
);

export const BarChartHorizontal: FC<BarChartHorizontalProps> = ({
  title,
  data,
  isLoading,
  xKey,
  yKey,
  size = 'medium',
  isEditCellSkeleton = false,
}) => {
  const yAxisWidth = yAxisWidths[size] || yAxisWidths.medium;

  const containerHeight = 57 * data.length;

  if (isLoading) {
    return (
      <div data-testid="bar-chart-skeleton">
        <BarChartHorizontalSkeleton yAxisWidth={yAxisWidth} />
      </div>
    );
  }

  return (
    <Card data-testid={CHARTS_TEST_IDS.BAR_CHART} className="p-4 border-custom-warm-grey-150">
      <CardContent>
        <div>
          <p className="text-body-14-semibold !text-custom-warm-grey-700 mb-6">{title}</p>
        </div>
        <div className={staticChartMountClassName}>
          <ResponsiveContainer width="100%" height={containerHeight} debounce={DEBOUNCE_TIME}>
            <BarChart
              margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
              data={data}
              layout="vertical"
            >
              <CartesianGrid vertical horizontal={false} stroke="#F0EEED" strokeDasharray="12 12" />

              <XAxis
                orientation="bottom"
                dataKey={yKey}
                type="number"
                axisLine={false}
                tickLine={false}
                mirror={false}
                tick={
                  isEditCellSkeleton
                    ? (props: SkeletonXYProps) => <BarChartSkeletonXAxisTick {...props} />
                    : {
                        fontSize: 12,
                        letterSpacing: '-0.01em',
                        color: '#A8A29D',
                      }
                }
              />

              <YAxis
                dataKey={xKey}
                type="category"
                axisLine={false}
                tickLine={false}
                mirror={false}
                width={yAxisWidth}
                tick={
                  isEditCellSkeleton
                    ? (props: SkeletonXYProps) => (
                        <BarChartSkeletonYAxisTick {...props} yAxisWidth={yAxisWidth} />
                      )
                    : createCustomYAxisTick(size)
                }
              />
              <Bar
                dataKey={yKey}
                barSize={BAR_HEIGHT}
                radius={[0, 6, 6, 0]}
                isAnimationActive={false}
              >
                {data.map((entry, index) => (
                  <Cell key={`bar-cell-${index}`} fill={entry.color ?? DEFAULT_LINE_COLOR} />
                ))}
                {!isEditCellSkeleton && (
                  <LabelList
                    dataKey={yKey}
                    position="insideLeft"
                    offset={10}
                    fill="#44403C"
                    fontSize={14}
                  />
                )}
              </Bar>

              <ReferenceLine x={0} stroke="#79716B" strokeWidth={1} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
